import React from 'react';
import { linkTo } from '@99designs/routing';
import { Site } from '@99designs/site-navigation/lib/Site';
import { trackEvent } from '@99designs/tracking';
import { COMPLETED_STEP_EVENT_NAME } from '../components/Onboarder/Config';
import { BudgetStepHeading, BudgetStepInputs, BudgetValue } from './BudgetStep';
import {
    DeliverableTypeStepHeading,
    DeliverableTypeStepInputs,
    DeliverableTypeValue,
} from './DeliverableTypeStep';
import { IndustryStepHeading, IndustryStepInput } from './IndustryStep';
import { PrimaryGoalStepHeading, PrimaryGoalStepInputs, PrimaryGoalValue } from './PrimaryGoalStep';
import { RecommendationPage } from './Recommendation';
import { StakeholderStepHeading, StakeholderStepInputs, StakeholderValue } from './StakeholderStep';
import { WebOnboarderLauncher } from './WebOnboarderLauncher';
import {
    WorkingMethodStepHeading,
    WorkingMethodStepInputs,
    WorkingMethodValue,
} from './WorkingMethodStep';
import { ONBOARDER_NAME } from './eventTracking';
import { getStepsResponse } from './getStepsResponse';
import type { CurrencyCode } from '@99designs/i18n';
import type { Role } from '@99designs/site-navigation/consts';
import type { Config, GenericStepData, Step } from '../components/Onboarder/Config';

export interface Data extends GenericStepData {
    stakeholder?: StakeholderValue;
    industry?: string;
    primaryGoal?: PrimaryGoalValue;
    deliverableType?: DeliverableTypeValue;
    budget?: BudgetValue;
    workingMethod?: WorkingMethodValue;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const steps = (data: Data): Step<Data>[] => {
    const steps: Step<Data>[] = [
        {
            key: 'website-for',
            title: __('Who is this website for?'),
            Heading: StakeholderStepHeading,
            Component: StakeholderStepInputs,
            canGoToNextStep: (data: Data) => Boolean(data.stakeholder),
            trackCompleted: (data: Data, stepNumber: number) => {
                trackEvent(COMPLETED_STEP_EVENT_NAME, {
                    onboarder_name: ONBOARDER_NAME,
                    step_name: 'stakeholder',
                    value: data.stakeholder || null,
                    step_number: stepNumber,
                });
            },
        },
        {
            key: 'primary-goal',
            Heading: PrimaryGoalStepHeading,
            Component: PrimaryGoalStepInputs,
            title: __('What is the primary goal of this website?'),
            canGoToNextStep: (data: Data) => Boolean(data.primaryGoal),
            trackCompleted: (data: Data, stepNumber: number) => {
                trackEvent(COMPLETED_STEP_EVENT_NAME, {
                    onboarder_name: ONBOARDER_NAME,
                    step_name: 'primaryGoal',
                    value: data.primaryGoal || null,
                    step_number: stepNumber,
                });
            },
        },
        {
            key: 'industry',
            title: __('What is the website industry?'),
            Heading: (props) => <IndustryStepHeading {...props} />,
            Component: (props) => <IndustryStepInput {...props} />,
            isSkippable: true,
            canGoToNextStep: (data: Data) => Boolean(data.industry),
            trackCompleted: (data: Data, stepNumber: number) => {
                trackEvent(COMPLETED_STEP_EVENT_NAME, {
                    onboarder_name: ONBOARDER_NAME,
                    step_name: 'industry',
                    value: data.industry || null,
                    step_number: stepNumber,
                });
            },
        },
        {
            key: 'deliverable-type',
            Heading: DeliverableTypeStepHeading,
            Component: DeliverableTypeStepInputs,
            title: __('What is the scope of the work you need done?'),
            canGoToNextStep: (data: Data) => Boolean(data.deliverableType),
            trackCompleted: (data: Data, stepNumber: number) => {
                trackEvent(COMPLETED_STEP_EVENT_NAME, {
                    onboarder_name: ONBOARDER_NAME,
                    step_name: 'deliverableType',
                    value: data.deliverableType || null,
                    step_number: stepNumber,
                });
            },
        },
    ];
    // eslint-disable-next-line eqeqeq
    if (data.deliverableType == 'complete-website') {
        steps.push({
            key: 'budget',
            Heading: BudgetStepHeading,
            Component: BudgetStepInputs,
            title: __('What is the budget for your website?'),
            canGoToNextStep: (data: Data) => Boolean(data.budget),
            trackCompleted: (data: Data, stepNumber: number) => {
                trackEvent(COMPLETED_STEP_EVENT_NAME, {
                    onboarder_name: ONBOARDER_NAME,
                    step_name: 'budget',
                    value: data.budget || null,
                    step_number: stepNumber,
                });
            },
        });
    } else {
        steps.push({
            key: 'working-method',
            Heading: WorkingMethodStepHeading,
            Component: WorkingMethodStepInputs,
            title: __('How would you like to work?'),
            canGoToNextStep: (data: Data) => Boolean(data.workingMethod),
            trackCompleted: (data: Data, stepNumber: number) => {
                trackEvent(COMPLETED_STEP_EVENT_NAME, {
                    onboarder_name: ONBOARDER_NAME,
                    step_name: 'workingMethod',
                    value: data.workingMethod || null,
                    step_number: stepNumber,
                });
            },
        });
    }
    return steps;
};

export const stepIsValid = (index: number): boolean => {
    return !!steps({})[index];
};

export const config = (role: Role, site: Site, exchangeRate: number): Config<Data> => {
    const currency = site.siteConfig['currency'] as CurrencyCode;

    return {
        steps,
        currency,
        exchangeRate,
        basePath: linkTo.webGetStarted(),
        Launcher: WebOnboarderLauncher,
        Recommendation: () => <RecommendationPage role={role} site={site} />,
        pageTitle: __('Compare website solutions'),
        storageKeyPrefix: 'webOnboarder',
        stepsResponse: (data: Data) => getStepsResponse(data, currency),
    };
};
